/**
 * Every path the admin panel asks the API for, in one place.
 *
 * Services import from here rather than writing string literals, so a route
 * that moves on the backend is a one-line edit in this file. Paths only — the
 * origin is `API_BASE_URL`, and the query string is built by the client from
 * the `query` option rather than spliced in here.
 */
export const API_ENDPOINTS = {
  auth: {
    /** Email + password. Mails a one-time code and answers with nothing more
     *  than confirmation that it was sent. */
    login: "/api/v1/backoffice/auth/login/",
    /** The six digits from the second step. Answers with the session. */
    verifyOtp: "/api/v1/backoffice/auth/login/verify/",
    /** Trades a refresh token for a new access/refresh pair, without asking
     *  the admin to sign in again. */
    refreshToken: "/api/v1/backoffice/auth/token/refresh/",
    /** Ends the session on the server side too, so the refresh token spent
     *  signing out cannot be replayed. Needs the access token on the
     *  request — see `client.ts`, which attaches it automatically. */
    logout: "/api/v1/backoffice/auth/logout/",
  },

  dashboard: {
    /**
     * The whole of the first screen in one answer: the headline figures, the
     * account split and renewal rate under them, and the two activity feeds.
     *
     * One route rather than four, which is why it is `overview` and not
     * `summary` — the summary is one object inside what it sends back.
     */
    overview: "/api/v1/backoffice/dashboard/",
  },

  payments: {
    /**
     * Every payment taken, or attempted. `status`, `from`, `to`, `page` and
     * `page_size` go on as query params, and the counts for all five tabs come
     * back with each page under `tab_counts`.
     *
     * Its own group rather than a route under `users`, because a payment is
     * not a fact about one account: this list is read across every account at
     * once, which is the whole point of the screen it feeds.
     */
    list: "/api/v1/backoffice/payments/",
  },

  users: {
    /**
     * One page of the roster, and the counts for the whole of it.
     *
     * `status`, `plan`, `search`, `page` and `page_size` go on as query
     * params. The counts come back with every page under `tab_counts`, which
     * is why there is no second route here for them — asking twice for
     * something sent unasked is two requests where the screen needs one.
     */
    list: "/api/v1/backoffice/users/",
    /**
     * One account, and one page of whichever of its two lists was asked for:
     * `view=details` for the clients on its roster, `view=billing_history` for
     * what it has been invoiced. `page` and `page_size` go on beside it.
     *
     * Both views answer with the account itself under `practitioner`, which is
     * why there is no separate route for the profile — the screen's header is
     * filled by whichever tab is open.
     *
     * The trailing slash is load-bearing: without it the server answers 301,
     * and a redirect is a second round trip at best.
     */
    detail: (workspaceId: number) =>
      `/api/v1/backoffice/users/${workspaceId}/`,
    /**
     * The PDF for one invoice. Answers with the file, not with JSON.
     *
     * Keyed on the invoice's `public_id` rather than the number printed on
     * it, and on the workspace as well — an invoice is only addressable
     * underneath the account it belongs to.
     */
    invoiceFile: (workspaceId: number, invoicePublicId: string) =>
      `/api/v1/backoffice/users/${workspaceId}/billing/${invoicePublicId}/pdf/`,
    /**
     * Mails a lapsed account an invitation to come back — the Expired tab's
     * one action. A POST with no body: what the mail says belongs to the
     * server, not to a console that would then own the copy.
     *
     * Keyed on the workspace, like `detail` above and for the same reason: the
     * roster is a list of workspaces, so that is the id a row carries. Answers
     * with `{ detail }` — a sentence about what it did, not the account.
     */
    reactivate: (workspaceId: number) =>
      `/api/v1/backoffice/users/${workspaceId}/reactivate/`,
  },
} as const;
